import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useEffect, useState} from 'react';

import type {JoinBody, PublicBallot, PublicPerson, VoteBody} from '../ballotTypes';
import {getRequest, postRequest} from '../utils/httpClient';

const base = (code: string) => `/api/public/ballots/${encodeURIComponent(code)}`;

export const usePublicBallot = (code: string) =>
    useQuery({queryKey: ['public', code], queryFn: () => getRequest<PublicBallot>(base(code))});

export const useJoin = (code: string) => {
    const client = useQueryClient();
    return useMutation({
        mutationFn: (body: JoinBody) => postRequest<PublicBallot>(`${base(code)}/join`, body),
        onSuccess: (ballot) => client.setQueryData(['public', code], ballot),
    });
};

export const useVote = (code: string) => {
    const client = useQueryClient();
    return useMutation({
        mutationFn: (body: VoteBody) => postRequest<{voted: true}>(`${base(code)}/vote`, body),
        onSuccess: () => client.invalidateQueries({queryKey: ['public', code]}),
    });
};

const MIN_QUERY = 2;

export const usePeopleSearch = (code: string, query: string) => {
    const [debounced, setDebounced] = useState(query.trim());

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(query.trim()), 250);
        return () => clearTimeout(timer);
    }, [query]);

    return useQuery({
        queryKey: ['public', code, 'people', debounced],
        enabled: debounced.length >= MIN_QUERY,
        queryFn: () =>
            getRequest<PublicPerson[]>(`${base(code)}/people?q=${encodeURIComponent(debounced)}`),
    });
};
